// components/CoursePlanner/PLCCapIndicator.tsx
// Displays Prior Learning Credit (PLC) usage toward the institution's cap
// (UMD 60, SJSU 30 — from institution config; UX-R1 task 1.1). Pass `cap`
// to override; otherwise it reads the active institution's value.
import { AlertTriangle } from 'lucide-react';

import { useCreditUnit, useInstitutionConfig } from '../../contexts/InstitutionConfigContext';

export interface PLCCapIndicatorProps {
  apCredits?: number;
  otherCredits?: number;
  /** Institution PLC cap. Defaults to the active institution's
   *  `priorLearningCap` from InstitutionConfigContext. */
  cap?: number;
  showBreakdown?: boolean;
  compact?: boolean;
}

const PLCCapIndicator = ({
  apCredits = 0,
  otherCredits = 0,
  cap,
  showBreakdown = true,
  compact = false,
}: PLCCapIndicatorProps) => {
  const { config } = useInstitutionConfig();
  const creditUnit = useCreditUnit();
  // PR #44 review follow-up: `??` passes a literal 0 through, and the
  // backend model has no gt=0 constraint — clamp non-positive caps to the
  // 60 default rather than rendering NaN%/x÷0.
  const requestedCap = cap ?? config.priorLearningCap;
  // `null` = the institution publishes no total PLC cap (UC Merced, W41 M5):
  // show credits used, no denominator / bar / over-cap warning. A non-positive
  // *number* is malformed, not "no cap" — clamp it to the 60 fallback.
  const hasCap = requestedCap !== null;
  const effectiveCap = hasCap ? (requestedCap > 0 ? requestedCap : 60) : 60;
  const totalCredits = apCredits + otherCredits;
  const percentage = hasCap ? Math.min((totalCredits / effectiveCap) * 100, 100) : 0;

  const getColorClass = (): string => {
    if (percentage >= 100) return 'bg-status-critical';
    if (percentage >= 83) return 'bg-status-warning';
    return 'bg-status-completed';
  };

  const getTextColorClass = (): string => {
    if (percentage >= 100) return 'text-status-critical-fg';
    if (percentage >= 83) return 'text-status-warning-fg';
    return 'text-ink-2';
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-3">PLC:</span>
          <span className={`text-xs font-medium ${getTextColorClass()}`}>
            {hasCap ? `${totalCredits}/${effectiveCap}` : `${totalCredits}`}
          </span>
        </div>
        {hasCap && percentage >= 100 && <AlertTriangle className="h-3 w-3 text-status-critical" />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 flex-1">
        {hasCap && (
          <div className="flex-1 h-2 bg-surface-sunken rounded-full overflow-hidden min-w-[80px] max-w-[120px]">
            <div
              className={`h-full ${getColorClass()} transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}

        <div className="flex items-center gap-1">
          <span className={`text-sm font-medium ${getTextColorClass()}`}>{totalCredits}</span>
          {hasCap ? (
            <>
              <span className="text-sm text-ink-3">/</span>
              <span className="text-sm text-ink-2">{effectiveCap}</span>
            </>
          ) : (
            <span className="text-sm text-ink-3">{creditUnit.plural}</span>
          )}
          {hasCap && percentage >= 100 && (
            <AlertTriangle className="h-3.5 w-3.5 text-status-critical ml-1" />
          )}
        </div>
      </div>

      {showBreakdown && (apCredits > 0 || otherCredits > 0) && (
        <div className="text-xs text-ink-3 hidden sm:block">
          {apCredits > 0 && <span>AP: {apCredits}</span>}
          {apCredits > 0 && otherCredits > 0 && <span className="mx-1">•</span>}
          {otherCredits > 0 && <span>Other: {otherCredits}</span>}
        </div>
      )}
    </div>
  );
};

export default PLCCapIndicator;
